import React, { useEffect, useRef, useState } from 'react';
import { CHAT } from '@/constants/testIds';
import { Waveform } from './Waveform';
import { PlayIcon, PauseIcon } from '@/lib/icons';

function formatDuration(seconds) {
  if (!seconds || !Number.isFinite(seconds)) return '0:00';
  const total = Math.max(0, Math.round(seconds));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Audio post: play button + waveform + duration. On play, words appear synced.
export const AudioPostPlayer = ({ audio }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [started, setStarted] = useState(false);
  const duration = audio?.duration || 0;
  const words = audio?.words || [];
  const transWords = audio?.translation_words || [];

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return undefined;
    const onTime = () => setCurrent(el.currentTime);
    const onPlay = () => {
      setPlaying(true);
      setStarted(true);
    };
    const onPause = () => setPlaying(false);
    const onEnded = () => {
      setPlaying(false);
      // keep transcript visible after playback ends
    };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('ended', onEnded);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('ended', onEnded);
    };
  }, []);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => {});
    } else {
      el.pause();
    }
  };

  const renderWordStates = (list) =>
    list.map((w, idx) => {
      const isActive = current >= w.start && current <= w.end;
      const isPlayed = current > w.end || (started && current >= w.end - 0.05);
      let cls = 'audio-transcript__word';
      if (isActive) cls += ' audio-transcript__word--active';
      else if (isPlayed) cls += ' audio-transcript__word--played';
      return (
        <span key={idx} className={cls} data-testid={CHAT.audioTranscriptWord}>
          {idx > 0 ? ' ' : ''}
          {w.word}
        </span>
      );
    });

  return (
    <div className="audio-post">
      <div className="audio-post__row">
        <button
          type="button"
          className="audio-post__play"
          onClick={toggle}
          aria-label={playing ? 'Pause' : 'Play'}
          data-testid={playing ? CHAT.audioPauseButton : CHAT.audioPlayButton}
        >
          {playing ? <PauseIcon size={18} /> : <PlayIcon size={18} />}
        </button>
        <Waveform
          duration={duration}
          maxWidth={260}
          height={22}
          dataTestId={CHAT.audioWaveform}
        />
        <span className="audio-post__duration" data-testid={CHAT.audioDuration}>
          {formatDuration(playing || current ? Math.max(0, duration - current) : duration)}
        </span>
      </div>

      {started && (words.length > 0 || transWords.length > 0) && (
        <div className="audio-transcript" data-testid={CHAT.audioTranscript}>
          {words.length > 0 && (
            <div className="audio-transcript__original">{renderWordStates(words)}</div>
          )}
          {transWords.length > 0 && (
            <div
              className="audio-transcript__translation"
              data-testid={CHAT.audioTranscriptTranslation}
            >
              {renderWordStates(transWords)}
            </div>
          )}
        </div>
      )}

      <audio ref={audioRef} src={audio?.data_url} preload="metadata" style={{ display: 'none' }} />
    </div>
  );
};
